import { useMemo, useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { COLORS } from '../../../theme/themeConstants';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import {
  Person,
  Search as SearchIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { useManageSettlements } from './useManageSettlements';
import { useGetSettlements } from '../../Query/SettlementsQuery';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';
import { HimmaContext } from '../../../Context/HimmaContext';
import { 
  InitiateSettlementPaymentRequest,
  InitiateSettlementPaymentRequestPaymentModeEnum,
  InitiateSettlementPaymentRequestPlatformEnum,
  InitiateSettlementPaymentRequestPaymentGatewayEnum
} from '../../../client-sdk';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}


const Settlements = () => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [openSettleModal, setOpenSettleModal] = useState(false);
  const [selectedTotalAmount, setSelectedTotalAmount] = useState(0);
  
  // Form state for settlement payment
  const [formData, setFormData] = useState({
    paymentMode: 'online',
    amount: 0,
    selectedPaymentMethod: 'hyperpay' as 'hyperpay' | 'stripe'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settlementIdsForPayment, setSettlementIdsForPayment] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { params, dispatch } = useManageSettlements();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: settlementsData, isLoading, refetch } = useGetSettlements(memoizedParams);
  
  // Get API context
  const api = useContext(HimmaContext);
  
  // Safety check for API context
  if (!api) {
    return (
      <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }
  
  // Show loading state when search is being processed
  const isSearching = searchTerm !== (params.search || '');

  // Debug: Log transaction types for mapping verification (remove in production)
  if (settlementsData?.data?.transactions) {
    // const transactionTypes = settlementsData.data.transactions.map(t => t.transactionType || 'unknown');
    // console.log('Transaction Types found:', [...new Set(transactionTypes)]);
    // console.log('Transaction count by type:', transactionTypes.reduce((acc, type) => {
    //   acc[type] = (acc[type] || 0) + 1;
    //   return acc;
    // }, {} as Record<string, number>));
  }

  // Use actual API data or fallback to default values


  // Calculate summary data based on transactionType from the actual transactions (kept for potential future use)
  // const calculateSummaryData = () => {
  //   const transactions = settlementsData?.data?.transactions || [];

  //   const toReceiveTransactions = transactions.filter(t => (t.transactionType || '').toLowerCase() === 'toreceive');
  //   const completedTransactions = transactions.filter(t => (t.transactionType || '').toLowerCase() === 'completed');

  //   return {
  //     toReceive: {
  //       amount: toReceiveTransactions.reduce((sum, t) => sum + (t.amount || 0), 0),
  //       transactions: toReceiveTransactions.length
  //     },
  //     completed: {
  //       amount: completedTransactions.reduce((sum, t) => sum + (t.amount || 0), 0),
  //       transactions: completedTransactions.length
  //     },
  //   };
  // };

  // const summaryData = calculateSummaryData(); // Commented out as it's no longer used

  const getFilteredTransactions = () => {
    // Ensure data.transactions exists and is an array
    const transactions = settlementsData?.data?.transactions || [];
    let filtered = transactions;

    // Apply status filter
    if (selectedStatus !== 'all') {
      const getStatusFromTransactionType = (transactionType: string): string => {
        const type = (transactionType || '').toLowerCase();
        if (type === 'toreceive') return 'toReceive';
        if (type === 'completed') return 'completed';
        if (type === 'topay') return 'toPay';
        if (type === 'refund') return 'refund';
        return ''; // Unknown type
      };
      
      filtered = filtered.filter(t => getStatusFromTransactionType(t.transactionType || '') === selectedStatus);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        (t.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.fromUser?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.toUser?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.orderInfo?.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.orderInfo?.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
      );
    }

    return filtered;
  };

  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value);
  };

  const handleApplyFilter = () => {
    // Map the selected status to the transaction type parameter
    // The API expects 'all' for all status, not undefined
    const transactionType = selectedStatus === 'all' ? 'all' : selectedStatus;
    
    // Dispatch the transaction type to the useManageSettlements hook
    dispatch({ type: 'SET_TRANSACTION_TYPE', payload: transactionType });
    
    // Reset to first page when applying filter
    dispatch({ type: 'SET_PAGE', payload: 1 });
  };

  const handleReceivePayment = () => {
    if (selectedTransactions.length > 0) {
      const totalAmount = getTotalAmountForSelectedTransactions();
      setSelectedTotalAmount(totalAmount);
      console.log(selectedTotalAmount);
      setFormData(prev => ({ ...prev, amount: totalAmount }));
      // Store the selected transactions for the API call
      setSettlementIdsForPayment([...selectedTransactions]);
      setOpenSettleModal(true);
      // Reset selected transactions after opening modal
      setSelectedTransactions([]);
    }
  };

  const getTotalAmountForSelectedTransactions = (): number => {
    const transactions = settlementsData?.data?.transactions || [];
    return selectedTransactions.reduce((total, transactionId) => {
      const transaction = transactions.find(t => t.transactionId === transactionId);
      return total + (transaction?.amount || 0);
    }, 0);
  };

  const handleFromDateChange = (newValue: Dayjs | null) => {
    setSelectedFromDate(newValue);
    // Dispatch the from date to the useManageSettlements hook
    const dateString = newValue ? newValue.toISOString() : undefined;
    // console.log('Selected From Date:', dateString);
    dispatch({ type: 'SET_FROM_DATE', payload: dateString });
  };

  const handleToDateChange = (newValue: Dayjs | null) => {
    setSelectedToDate(newValue);
    // Dispatch the to date to the useManageSettlements hook
    const dateString = newValue ? newValue.toISOString() : undefined;
    // console.log('Selected To Date:', dateString);
    dispatch({ type: 'SET_TO_DATE', payload: dateString });
  };

  // Handle reset functionality - optimized to avoid unnecessary API calls
  const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    setIsResetting(true);
    
    // Reset local state first
    setSelectedStatus('all');
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setSearchTerm('');
    setSelectedTransactions([]);
    setSelectedTotalAmount(0);
    
    // Reset params without triggering immediate API call
    dispatch({ type: 'SET_FROM_DATE', payload: undefined });
    dispatch({ type: 'SET_TO_DATE', payload: undefined });
    dispatch({ type: 'SET_SEARCH', payload: undefined });
    dispatch({ type: 'SET_TRANSACTION_TYPE', payload: undefined });
    dispatch({ type: 'SET_PAGE', payload: 1 });
    
    // Reset the flag after a brief delay to allow state updates
    setTimeout(() => {
      setIsResetting(false);
    }, 100);
  };

  // Handle settlement payment submission
  const handleSubmitSettlement = async () => {
    if (isSubmitting) return;
    
    // Validate required fields
    if (!formData.amount || formData.amount <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    if (!formData.selectedPaymentMethod) {
      alert('Please select a payment method');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get userId from localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userId = userData?.userId;
      
      if (!userId) {
        alert('User ID not found. Please login again.');
        return;
      }

      // Prepare API request for online payment
      const apiRequest: InitiateSettlementPaymentRequest = {
        userId: userId,
        settlementIds: settlementIdsForPayment,
        paymentMode: InitiateSettlementPaymentRequestPaymentModeEnum.Online,
        amount: formData.amount,
        platform: InitiateSettlementPaymentRequestPlatformEnum.Web,
        paymentGateway: formData.selectedPaymentMethod === 'hyperpay' 
          ? InitiateSettlementPaymentRequestPaymentGatewayEnum.Hyperpay 
          : InitiateSettlementPaymentRequestPaymentGatewayEnum.Stripe
      };

      console.log('Settlement API Request:', apiRequest);

      // Call the API
      const response = await api.initiateSettlementPayment(apiRequest);
      console.log('Settlement API Response:', response);

      // Handle success - redirect to payment gateway
      if (response.data && response.data.paymentMode === 'ONLINE') {
        const onlineResponse = response.data as any; // Type assertion for online response
        
        if (formData.selectedPaymentMethod === 'hyperpay' && onlineResponse.checkoutId) {
          // For HyperPay, navigate to the wizard page with checkoutId
          console.log('Redirecting to HyperPay wizard page:', onlineResponse.checkoutId);
          
          // Save to localStorage as backup
          try {
            localStorage.setItem('hyperpay_checkout_id', onlineResponse.checkoutId);
            localStorage.setItem('hyperpay_merchant_id', userId);
            localStorage.setItem('hyperpay_settlement_payment_id', onlineResponse.settlementPaymentId || '');
          } catch (err) {
            console.error('Failed to save to localStorage:', err);
          }
          
          // Close modal and navigate to payment wizard
          setOpenSettleModal(false);
          setSelectedTotalAmount(0);
          setFormData({
            paymentMode: 'online',
            amount: 0,
            selectedPaymentMethod: 'hyperpay'
          });
          setSettlementIdsForPayment([]);
          
          // Navigate to payment wizard
          window.location.href = `/payment/settlement/hyperpay/wizard?checkoutId=${onlineResponse.checkoutId}&settlementPaymentId=${onlineResponse.settlementPaymentId || ''}&merchantId=${userId}`;
        } else if (formData.selectedPaymentMethod === 'stripe' && onlineResponse.checkoutUrl) {
          // For Stripe, redirect to checkout URL
          window.location.href = onlineResponse.checkoutUrl;
        } else {
          alert('Failed to get payment checkout information from server');
        }
      } else {
        alert('Unexpected response format from server');
      }
    } catch (error: any) {
      console.error('Error initiating settlement payment:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to initiate settlement payment';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if both dates are selected
  const bothDatesSelected = selectedFromDate && selectedToDate;

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Use effect to dispatch search when debounced search changes
  useEffect(() => {
    // Skip API calls during reset to prevent loading state
    if (isResetting) return;
    
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    // Reset to first page when searching
    if (debouncedSearch !== params.search) {
      dispatch({ type: 'SET_PAGE', payload: 1 });
    }
  }, [debouncedSearch, dispatch, params.search, isResetting]);

  // Export to Excel function
  const handleExportToExcel = () => {
    const filteredData = getFilteredTransactions();
    
    // Prepare data for export
    const exportData = filteredData.map((transaction, index) => ({
      'S.No': index + 1,
      'Order Number': transaction.orderNumber || 'N/A',
      'From User Type': transaction.fromUser?.userType || 'N/A',
      'To User Type': transaction.toUser?.userType || 'N/A',
      'Transaction Category': transaction.transactionCategory || 'N/A',
      'Amount (JOD)': transaction.amount || 0,
      'Type': transaction.transactionType || 'N/A',
      'Status': transaction.status || 'N/A',
      'Description': transaction.description || 'N/A',
      'Date': transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString() : 'N/A',
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 8 },   // S.No
      { wch: 25 },  // Order Number
      { wch: 15 },  // From User Type
      { wch: 15 },  // To User Type
      { wch: 20 },  // Transaction Category
      { wch: 15 },  // Amount
      { wch: 15 },  // Type
      { wch: 15 },  // Status
      { wch: 30 },  // Description
      { wch: 15 },  // Date
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Settlements');

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `Settlements_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Export to PDF function
  const handleExportToPDF = () => {
    const filteredData = getFilteredTransactions();
    
    // Create new PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation for better table fit
    
    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Settlements Report', 14, 15);
    
    // Add date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);
    
    // Add summary information
    doc.text(`Total Records: ${filteredData.length}`, 14, 35);
    
    // Prepare table data
    const tableData = filteredData.map((transaction, index) => [
      index + 1,
      transaction.orderNumber || 'N/A',
      transaction.fromUser?.userType || 'N/A',
      transaction.toUser?.userType || 'N/A',
      transaction.transactionCategory || 'N/A',
      transaction.amount || 0,
      transaction.transactionType || 'N/A',
      transaction.status || 'N/A',
    ]);

    // Add table using autoTable plugin
    autoTable(doc, {
      head: [['S.No', 'Order Number', 'From User', 'To User', 'Transaction Category', 'Amount (JOD)', 'Type', 'Status']],
      body: tableData,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [139, 69, 19], // Brown color matching the theme
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 }, // S.No
        1: { cellWidth: 35 }, // Order Number
        2: { cellWidth: 25 }, // From User
        3: { cellWidth: 25 }, // To User
        4: { cellWidth: 35 }, // Transaction Category
        5: { halign: 'center', cellWidth: 20 }, // Amount
        6: { cellWidth: 25 }, // Type
        7: { cellWidth: 25 }, // Status
      },
    });

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `Settlements_${currentDate}.pdf`;

    // Save file
    doc.save(filename);
  };


  // const getStatusChip = (status: string, date: string) => {
  //   const statusConfig: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'error'; icon: React.ReactElement }> = {
  //     'PENDING': { label: 'Pending', color: 'warning', icon: <Schedule /> },
  //     'IN_PROGRESS': { label: 'In Progress', color: 'info', icon: <Schedule /> },
  //     'COMPLETED': { label: 'Completed', color: 'success', icon: <CheckCircle /> },
  //     'FAILED': { label: 'Failed', color: 'error', icon: <Schedule /> },
  //   };

  //   const config = statusConfig[status] || statusConfig['PENDING'];
  //   const formattedDate = new Date(date).toLocaleDateString();

  //   return (
  //     <Box display="flex" alignItems="center" gap={1}>
  //       <Chip
  //         label={config.label}
  //         size="small"
  //         color={config.color}
  //         icon={config.icon}
  //       />
  //       <Typography variant="caption" color="text.secondary">
  //         {formattedDate}
  //       </Typography>
  //     </Box>
  //   );
  // };



  // Grid columns for To Receive tab
  const toReceiveColumns = useMemo<GridColDef[]>(() => [
    {
      field: 'sno',
      headerName: t('settlements.columns.sno'),
      flex: 0.5,
      renderCell: (params) => params.row.sno,
    },
    {
      field: 'orderId',
      headerName: t('settlements.columns.orderNumber'),
      flex: 1,
      renderCell: (params) => params.row.orderNumber || 'N/A',
    },
    {
      field: 'fromUserType',
      headerName: t('settlements.columns.fromUserType'),
      flex: 1,
      renderCell: (params) => params.row.fromUserType || 'N/A',
    },
    {
      field: 'toUserType',
      headerName: t('settlements.columns.toUserType'),
      flex: 1,
      renderCell: (params) => params.row.toUserType || 'N/A',
    },
    {
      field: 'description',
      headerName: t('settlements.columns.description'),
      flex: 1,
      renderCell: (params) => params.row.description || 'N/A',
    },
    {
      field: 'amount',
      headerName: `${t('settlements.columns.amount')} (JOD)`,
      flex: 1,
      renderCell: (params) => params.row.amount || 'N/A',
    },
    {
      field: 'transactionType',
      headerName: t('settlements.columns.type'),
      flex: 1,
      renderCell: (params) => params.row.transactionType || 'N/A',
    },
    {
      field: 'status',
      headerName: t('settlements.columns.status'),
      flex: 1,
      renderCell: (params) => params.row.status || 'N/A',
    },
  ], [t]);

  // Grid columns for Completed tab (kept for potential future use)
  // const completedColumns = useMemo<GridColDef[]>(() => [
  //   {
  //     field: 'sno',
  //     headerName: t('settlements.columns.sno'),
  //     flex: 0.5,
  //     renderCell: (params) => params.row.sno,
  //   },
  //   {
  //     field: 'orderId',
  //     headerName: t('settlements.columns.orderNumber'),
  //     flex: 1,
  //     renderCell: (params) => params.row.orderId || 'N/A',
  //   },
  //   {
  //     field: 'fromUserType',
  //     headerName: t('settlements.columns.fromUserType'),
  //     flex: 1,
  //     renderCell: (params) => params.row.fromUserType || 'N/A',
  //   },
  //   {
  //     field: 'toUserType',
  //     headerName: t('settlements.columns.toUserType'),
  //     flex: 1,
  //     renderCell: (params) => params.row.toUserType || 'N/A',
  //   },
  //   {
  //     field: 'transactionCategory',
  //     headerName: t('settlements.columns.transactionCategory'),
  //     flex: 1,
  //     renderCell: (params) => params.row.transactionCategory || 'N/A',
  //   },
  //   {
  //     field: 'amount',
  //     headerName: t('settlements.columns.amount'),
  //     flex: 1,
  //     renderCell: (params) => params.row.amount || 'N/A',
  //   },
  //   {
  //     field: 'transactionType',
  //     headerName: t('settlements.columns.type'),
  //     flex: 1,
  //     renderCell: (params) => params.row.transactionType || 'N/A',
  //   },
  //   {
  //     field: 'status',
  //     headerName: t('settlements.columns.status'),
  //     flex: 1,
  //     renderCell: (params) => params.row.status || 'N/A',
  //   },
  //   {
  //     field: 'actions',
  //     headerName: t('settlements.columns.actions'),
  //     flex: 1,
  //     renderCell: () => (
  //       <Box display="flex" gap={1}>
  //         <Button
  //           variant="outlined"
  //           size="small"
  //           sx={{
  //             borderColor: '#0288d1',
  //             color: '#0288d1',
  //             '&:hover': {
  //               borderColor: '#01579b',
  //               backgroundColor: 'rgba(2, 136, 209, 0.04)'
  //             },
  //           }}
  //         >
  //           {t('settlements.actions.viewDetails')}
  //         </Button>
  //       </Box>
  //     ),
  //   },
  // ], [t]);



  // Prepare data for grid based on active tab
  const getGridData = () => {
    const filteredData = getFilteredTransactions();
    return filteredData.map((transaction, index) => ({
      ...transaction,
      id: transaction.transactionId, // Use transactionId as the unique identifier
      sno: index + 1,
    }));
  };

  // Get columns - use a unified set of columns
  const getColumns = () => {
    return toReceiveColumns; // Use the same columns for all transactions
  };

  // Previously used to switch button text; no longer needed after conditional rendering

  // Get action button color based on selected status
  const getActionButtonColor = () => {
    if (selectedStatus === 'completed') {
      return 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)';
    }
    return 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)';
  };

  // Get action button hover color based on selected status
  const getActionButtonHoverColor = () => {
    if (selectedStatus === 'completed') {
      return 'linear-gradient(135deg, #01579b 0%, #03a9f4 100%)';
    }
    return 'linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)';
  };

  // Show loading state while data is being fetched (but not during reset)
  if (isLoading && !settlementsData && !isResetting) {
    return (
      <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6">{t('settlements.messages.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" color={COLORS.secondary.main}>
          {t('settlements.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: COLORS.primary.main,
            color: COLORS.secondary.main,
            borderRadius: 1,
            fontWeight: 'bold',
          }}
        >
          <Person />
          {t('settlements.merchant')}
        </Box>
      </Box>

      {/* All Filters in One Line */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        {/* Status Filter */}
        <Typography variant="body1" color="text.primary">
          {t('settlements.filters.status')}
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            value={selectedStatus}
            onChange={handleStatusChange}
            displayEmpty
            sx={{
              height: 40,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ddd',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary.main,
              },
            }}
          >
            <MenuItem value="all">{t('settlements.status.allStatus')}</MenuItem>
            <MenuItem value="all">{t('settlements.status.all')}</MenuItem>
            <MenuItem value="toPay">{t('settlements.summary.toPay')}</MenuItem>
            <MenuItem value="toReceive">{t('settlements.summary.toReceive')}</MenuItem>
            <MenuItem value="completed">{t('settlements.summary.completed')}</MenuItem>
          </Select>
        </FormControl>

        {/* Date Filters */}
        <Typography variant="body1" color={bothDatesSelected ? 'text.primary' : 'text.secondary'}>
          {t('settlements.filters.fromDate')}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedFromDate}
            onChange={handleFromDateChange}
            slotProps={{
              textField: {
                placeholder: t('settlements.filters.pickFromDate'),
                sx: { minWidth: 200 }
              }
            }}
          />
        </LocalizationProvider>

        <Typography variant="body1" color={bothDatesSelected ? 'text.primary' : 'text.secondary'}>
          {t('settlements.filters.toDate')}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedToDate}
            onChange={handleToDateChange}
            slotProps={{
              textField: {
                placeholder: t('settlements.filters.pickToDate'),
                sx: { minWidth: 200 }
              }
            }}
          />
        </LocalizationProvider>

        {/* Apply Button */}
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          sx={{
            backgroundColor: COLORS.primary.main,
            color: COLORS.secondary.main,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            px: 2,
            py: 1,
            borderRadius: 2,
            minWidth: 100,
            height: 40,
            '&:hover': {
              backgroundColor: COLORS.primary.dark,
            },
          }}
        >
          {t('settlements.actions.apply')}
        </Button>

        {/* Reset Button */}
        <Button
          type="button"
          variant="outlined"
          onClick={handleReset}
          sx={{
            backgroundColor: 'white',
            borderColor: '#8B4513',
            color: '#8B4513',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            px: 2,
            py: 1,
            borderRadius: 2,
            minWidth: 100,
            height: 40,
            '&:hover': {
              backgroundColor: 'rgba(139, 69, 19, 0.04)',
              borderColor: '#A0522D',
              color: '#654321',
            },
          }}
        >
          {t('settlements.actions.reset')}
        </Button>
      </Box>

      {/* Date Selection Helper */}
      {!bothDatesSelected && (selectedFromDate || selectedToDate) && (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', color: 'red' }}>
            {t('settlements.filters.selectBothDates')}
          </Typography>
        </Box>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
        {/* To Pay */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>{t('settlements.summary.toPay') || 'To Pay'}</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {Number(settlementsData?.data?.summary?.totalAmountToPay).toFixed(2) ?? 0}
            </Typography>
            <Typography variant="body2">
              {(settlementsData?.data?.summary?.countToPay ?? 0)} {t('settlements.summary.transactions')}
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>{t('settlements.summary.toReceive')}</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {Number(settlementsData?.data?.summary?.totalAmountToReceive).toFixed(2) ?? 0}
            </Typography>
            <Typography variant="body2">
              {(settlementsData?.data?.summary?.countToReceive ?? 0)} {t('settlements.summary.transactions')}
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>{t('settlements.summary.completed')}</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {Number(settlementsData?.data?.summary?.totalAmountCompleted).toFixed(2) ?? 0}
            </Typography>
            <Typography variant="body2">
              {(settlementsData?.data?.summary?.countCompleted ?? 0)} {t('settlements.summary.transactions')}
            </Typography>
          </CardContent>
        </Card>
      </Box>


      {/* Content based on active tab - Grid Layout for all tabs */}
      <Box sx={{ p: 2, border: '1px solid #e0e0e0', marginTop: '20px', color: '#666' }}>
        {/* Search and Action Buttons */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Search */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder={isSearching ? t('settlements.search.searching') : t('settlements.search.placeholder')}
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none',
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: isSearching ? COLORS.primary.main : '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Export Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleExportToExcel}
              startIcon={<ExcelIcon />}
              sx={{
                backgroundColor: 'white',
                borderColor: '#4caf50',
                color: '#4caf50',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                px: 2,
                py: 1,
                borderRadius: 2,
                minWidth: 150,
                height: 40,
                borderWidth: 2,
                '&:hover': {
                  backgroundColor: '#4caf50',
                  color: 'white',
                  borderColor: '#4caf50',
                  borderWidth: 2,
                },
              }}
            >
              {t('settlements.actions.exportExcel')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportToPDF}
              startIcon={<PdfIcon />}
              sx={{
                backgroundColor: 'white',
                borderColor: '#f44336',
                color: '#f44336',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                px: 2,
                py: 1,
                borderRadius: 2,
                minWidth: 150,
                height: 40,
                borderWidth: 2,
                '&:hover': {
                  backgroundColor: '#f44336',
                  color: 'white',
                  borderColor: '#f44336',
                  borderWidth: 2,
                },
              }}
            >
              {t('settlements.actions.exportPdf')}
            </Button>
          </Box>

          {/* Action Buttons (only show when status is To Pay) */}
          {selectedStatus === 'toPay' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                disabled={selectedTransactions.length === 0}
                onClick={handleReceivePayment}
                sx={{
                  background: selectedTransactions.length > 0
                    ? getActionButtonColor()
                    : '#94a3b8',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  minWidth: 150,
                  boxShadow: selectedTransactions.length > 0
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  '&:hover': {
                    background: selectedTransactions.length > 0
                      ? getActionButtonHoverColor()
                      : '#94a3b8',
                    boxShadow: selectedTransactions.length > 0
                      ? '0 6px 8px -1px rgba(0, 0, 0, 0.15)'
                      : 'none',
                  },
                }}
              >
                {t('settlements.actions.receivePayment')} ({selectedTransactions.length})
              </Button>
            </Box>
          )}
        </Box>

        {/* Data Grid */}
        <CommonDataGrid
          key={`settlements-grid-${params.page}-${params.limit}-${params.search || 'no-search'}`}
          rows={getGridData()}
          columns={getColumns()}
          pageSize={params.limit}
          onPageSizeChange={(newSize) => dispatch({ type: 'SET_LIMIT', payload: newSize })}
          loading={isLoading && !isResetting}
          checkboxSelection
          selectionModel={selectedTransactions}
          onRowSelectionModelChange={setSelectedTransactions}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('settlements.messages.noTransactions')} onRetry={() => refetch()} />
          }}
          onPageChange={(newPage) => dispatch({ type: 'SET_PAGE', payload: newPage + 1 })}
          rowCount={settlementsData?.data?.pagination?.totalItems || getGridData().length}
          page={params.page - 1}
        />
      </Box>

      {/* Settle Modal */}
      <Dialog 
        open={openSettleModal} 
        onClose={() => {
          setOpenSettleModal(false);
          setSelectedTotalAmount(0);
          setFormData({
            paymentMode: 'online',
            amount: 0,
            selectedPaymentMethod: 'hyperpay'
          });
          setSettlementIdsForPayment([]);
        }} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: 'auto',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: COLORS.primary.main, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Settle Payment
          <IconButton
            onClick={() => {
              setOpenSettleModal(false);
              setSelectedTotalAmount(0);
              setFormData({
                paymentMode: 'online',
                amount: 0,
                selectedPaymentMethod: 'hyperpay'
              });
              setSettlementIdsForPayment([]);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ml: 20 }}>
            {/* Payment Mode */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, minWidth: '140px' }}>
                Payment Mode *
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup row value="online">
                  <FormControlLabel
                    value="online"
                    control={<Radio size="small" sx={{ color: COLORS.primary.main, '&.Mui-checked': { color: COLORS.primary.main } }} />}
                    label={<Typography variant="body2">online</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Payment Methods */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, minWidth: '140px' }}>
                Payment Methods *
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup 
                  row 
                  value={formData.selectedPaymentMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, selectedPaymentMethod: e.target.value as 'hyperpay' | 'stripe' }))}
                >
                  <FormControlLabel
                    value="hyperpay"
                    control={<Radio size="small" sx={{ color: COLORS.primary.main, '&.Mui-checked': { color: COLORS.primary.main } }} />}
                    label={<Typography variant="body2">Hyperpay</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Amount */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, minWidth: '140px' }}>
                Amount (JOD) *
              </Typography>
              <TextField
                type="number"
                placeholder="Enter Amount"
                variant="outlined"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                  }
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenSettleModal(false);
                  setSelectedTotalAmount(0);
                  setFormData({
                    paymentMode: 'online',
                    amount: 0,
                    selectedPaymentMethod: 'hyperpay'
                  });
                  setSettlementIdsForPayment([]);
                }}
                sx={{ 
                  textTransform: 'none',
                  minWidth: '100px',
                  height: '40px'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitSettlement}
                disabled={isSubmitting}
                sx={{ 
                  textTransform: 'none',
                  minWidth: '100px',
                  height: '40px',
                  backgroundColor: COLORS.primary.main,
                  '&:hover': {
                    backgroundColor: COLORS.primary.dark,
                  }
                }}
              >
                {isSubmitting ? 'Processing...' : 'Submit'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Settlements;